
import React from 'react';
import { Search, Percent, Mail, MapPin } from 'lucide-react';

interface FooterProps {
  onShowPrivacy: () => void;
  onShowTerms: () => void;
}

const Footer: React.FC<FooterProps> = ({ onShowPrivacy, onShowTerms }) => {
  const scrollToSignup = () => {
    const element = document.getElementById('signup');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center relative overflow-hidden">
                <div className="relative">
                  <Search className="w-4 h-4 text-white absolute top-0 left-0" />
                  <Percent className="w-3 h-3 text-white absolute top-1 left-2" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform rotate-45"></div>
              </div>
              <span className="text-2xl font-bold">Discount Pro</span>
            </div>
            <p className="text-gray-300 mb-4 max-w-md">
              We're the pros at finding the best discounts anywhere to save you serious money, anytime, anywhere. 
              Join thousands of smart savers who trust us for verified deals and exclusive coupons.
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-300">
                <Mail className="w-4 h-4" />
                <span>help@discountpro.org</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300">
                <MapPin className="w-4 h-4" />
                <span>discountpro.org</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-300 hover:text-green-400 transition-colors"
                >
                  Home
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('deals')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-300 hover:text-green-400 transition-colors"
                >
                  Current Deals
                </button>
              </li>
              <li>
                <button 
                  onClick={scrollToSignup}
                  className="text-gray-300 hover:text-green-400 transition-colors"
                >
                  Join Free
                </button>
              </li>
              <li>
                <a href="mailto:help@discountpro.org" className="text-gray-300 hover:text-green-400 transition-colors">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Legal & Trust */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Legal & Trust</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={onShowPrivacy}
                  className="text-gray-300 hover:text-green-400 transition-colors"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button 
                  onClick={onShowTerms}
                  className="text-gray-300 hover:text-green-400 transition-colors"
                >
                  Terms of Service
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              <p>© 2025 Discount Pro. All rights reserved.</p>
              <p className="mt-1">
                We respect your privacy and are committed to protecting your personal information. 
                You can unsubscribe from our emails at any time.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={scrollToSignup}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Start Saving Today
              </button>
            </div>
          </div>
        </div>

        {/* CAN-SPAM Compliance Notice */}
        <div className="mt-8 p-4 bg-gray-800 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-300 mb-2">Email Communication Policy</h4>
          <p className="text-xs text-gray-400 leading-relaxed">
            By joining our email list, you consent to receive promotional emails from Discount Pro at the email address provided. 
            We are committed to protecting your privacy and complying with the CAN-SPAM Act. You can unsubscribe at any time by 
            clicking the unsubscribe link in any email or by contacting us at help@discountpro.org. We will never sell, rent, 
            or share your email address with third parties for their marketing purposes. Our emails will always include our 
            physical address and clear identification that the message is from Discount Pro.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
